/**
 * @module ui.accordion
 * @version 4.6.0
 * @description LIFTR Accordion Web Component mit Keyboard Navigation, ARIA & Sheet Mode
 * 
 * Migration von v4.0:
 * - ROLE → ROLES (Plural)
 * - STATES, EVENTS, SETTINGS Konstanten hinzugefügt
 * - String Literals → Konstanten
 * - liftr-accordion-mode/icon → liftr-settings (space-separated tokens)
 * 
 * @changelog
 * - 4.6.0: CONFIG BOUNDARY
 *        - REMOVE: #getSetting() bridge method (anti-pattern)
 *        - REMOVE: Overlap keys from DEFAULTS (mode, exclusive, icon, size, enabled)
 *        - ADD: #css() accessor for CSS_SCHEMA values
 *        - FIX: CSS_SCHEMA.icon.default aligned to 'plus' (was 'angle')
 *        - RULE: DEFAULTS = static JS-only config, CSS_SCHEMA = responsive CSS config
 * - 4.5.0: SHEET MODE 
 *        - ADD: mode-sheet für responsive bottom-sheet panels
 *        - ADD: On-demand <liftr-panel> creation per accordion item
 *        - ADD: Content portaling (accordion-panel → panel_module → zurück)
 *        - ADD: LISTEN.PANEL_CLOSED for sheet close handling
 *        - ADD: SETTINGS.MODE_SHEET token
 * - 4.4.1: AUDIT FIX - CSS_SCHEMA Object.freeze() for v4.5 compliance
 * - 4.4.0: PUBLIC API COMPLIANCE
 *        - ADD: get openIndex getter (for exclusive mode)
 *        - ADD: get openIndices getter (for non-exclusive mode)
 * - 4.3.0: Initial v4.3 implementation
 * 
 * @element liftr-accordion
 * 
 * @fires liftr:accordion:opened - Panel wurde geöffnet
 * @fires liftr:accordion:closed - Panel wurde geschlossen
 * @fires liftr:accordion:enabled - Accordion wurde aktiviert
 * @fires liftr:accordion:disabled - Accordion wurde deaktiviert
 * @fires liftr:accordion:settings-changed - CSS Settings geändert
 * @fires liftr:accordion:keyboard - Keyboard Navigation
 * 
 * @attr {string} liftr-id - Optional identifier
 * @attr {string} liftr-state - State output (disabled)
 * @attr {string} liftr-settings - Settings output (mode-* icon-* size-*)
 * 
 * DOM Setup:
 *   <liftr-accordion liftr-state="loading" liftr-skeleton>
 *     <div liftr-role="accordion-item" id="faq-1">
 *       <button liftr-role="accordion-toggle">Question</button>
 *       <div liftr-role="accordion-panel">Answer</div>
 *     </div>
 *   </liftr-accordion>
 * 
 * CSS Settings Properties (read from root via getComputedStyle):
 *   --accordion-enabled: true|false
 *   --accordion-mode: highlight|closed|sheet
 *   --accordion-exclusive: true|false
 *   --accordion-icon: none|angle|plus|chevron|arrow|bell
 *   --accordion-size: xsmall|small|regular|medium|large
 * 
 * Output Attribute (liftr-settings):
 *   liftr-settings="mode-closed icon-angle size-regular"
 *   liftr-settings="mode-highlight icon-none"
 * 
 * JS Config (via settings.modules.accordion):
 *   initialOpen: number|null (index of initially open item)
 */

import { LiftrModule } from './core.module.js';
import { ATTRIBUTES, i18n } from './core.liftr.js';
import { ensureId, on } from './core.utilities.js';

/* ═══════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════ */

const ROLES = Object.freeze({
  ITEM:   'accordion-item',
  TOGGLE: 'accordion-toggle',
  PANEL:  'accordion-panel'
});

const STATES = Object.freeze({
  DISABLED: 'disabled',
  OPEN:     'open',
  CLOSED:   'closed'
});

const EVENTS = Object.freeze({
  OPENED:           'opened',
  CLOSED:           'closed',
  ENABLED:          'enabled',
  DISABLED:         'disabled',
  SETTINGS_CHANGED: 'settings-changed',
  KEYBOARD:         'keyboard'
});

const SETTINGS = Object.freeze({
  MODE_CLOSED:    'mode-closed',
  MODE_HIGHLIGHT: 'mode-highlight',
  MODE_SHEET:     'mode-sheet',
  ICON_NONE:      'icon-none',
  ICON_ANGLE:     'icon-angle',
  ICON_PLUS:      'icon-plus',
  ICON_CHEVRON:   'icon-chevron',
  ICON_ARROW:     'icon-arrow',
  ICON_BELL:      'icon-bell',
  SIZE_XSMALL:    'size-xsmall',
  SIZE_SMALL:     'size-small',
  SIZE_REGULAR:   'size-regular',
  SIZE_MEDIUM:    'size-medium',
  SIZE_LARGE:     'size-large'
});

const KEYS = Object.freeze({
  ARROW_UP:   'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  HOME:       'Home',
  END:        'End'
});

/**
 * External events this module listens to.
 * NOT exported — internal only.
 * @private
 */
const LISTEN = Object.freeze({
  PANEL_CLOSED: 'liftr:panel:closed'
});

const CSS_SCHEMA = Object.freeze({
  mode:      { type: 'string',  default: 'closed' },
  exclusive: { type: 'bool',    default: true },
  icon:      { type: 'string',  default: 'plus' },
  size:      { type: 'string',  default: 'regular' }
});

function getHashFromURL() {
  try {
    return window.location.hash.slice(1) || null;
  } catch {
    return null;
  }
}

function setHashToURL(hash) {
  try {
    if (hash) {
      history.replaceState(null, '', `#${hash}`);
    } else {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  } catch {}
}

function buildSettingsTokens(mode, icon, size) {
  const tokens = [];
  tokens.push(`mode-${mode}`);
  if (icon === false || icon === 'false' || icon === 'none') {
    tokens.push('icon-none');
  } else {
    tokens.push(`icon-${icon}`);
    tokens.push(`size-${size || 'regular'}`);
  }
  return tokens.join(' ');
}

class LiftrAccordion extends LiftrModule {
  
  static NAME = 'accordion';
  
  static DEFAULTS = {
    initialOpen: null
  };
  
  /** @type {AccordionItem[]} */
  #items = [];
  /** @type {object|null} */
  #watcher = null;
  /** @type {boolean} */
  #isDisabled = false;
  /** @type {boolean} */
  #hasInitialized = false;
  /** @type {object|null} CSS settings cache */
  #cssSettings = null;
  /** @type {string|null} */
  #currentMode = null;
  /** @type {string|null} */
  #currentIcon = null;
  /** @type {string|null} */
  #currentSize = null;
  
  /** @type {Map<number, { panel: HTMLElement, placeholder: Comment }>} Sheet panels per item index */
  #sheets = new Map();
  
  /** @type {number|null} Index of currently open sheet */
  #activeSheetIndex = null;
  
  /** @type {Function|null} Disposable for panel:closed listener */
  #panelCloseDisposable = null;
  
  /** @type {Object|null} Cached ROLES from ui.panel.js (lazy loaded) */
  #panelRoles = null;
  
  mount() {
    this.#setupItems();
    this.#attachListeners();
    
    this.#watcher = this.watchSettings('accordion', {
      enabled: '--accordion-enabled',
      css: CSS_SCHEMA,
      debounce: 100
    }, {
      onEnable: (s) => this.#onEnable(s),
      onDisable: (s) => this.#onDisable(s),
      onChange: (changes) => this.#onChange(changes)
    });
    
    this.#isDisabled = !this.#watcher.isEnabled();
    
    if (!this.#isDisabled) {
      this.#initialSetup();
    }
  }
  
  unmount() {
    this.#closeActiveSheet();
    this.#destroyAllSheets();
    this.#items = [];
    this.#cssSettings = null;
    this.#hasInitialized = false;
    super.unmount();
  }
  
  #createItem(itemEl, index) {
    const accordion = this;
    const toggleBtn = this.#findDirectRole(itemEl, ROLES.TOGGLE);
    const panelEl = this.#findDirectRole(itemEl, ROLES.PANEL);
    
    if (!toggleBtn) {
      throw new Error(`Item ${index} missing toggle (liftr-role="${ROLES.TOGGLE}")`);
    }
    if (!panelEl) {
      throw new Error(`Item ${index} missing panel (liftr-role="${ROLES.PANEL}")`);
    }
    if (toggleBtn.tagName !== 'BUTTON') {
      throw new Error(`Item ${index}: Toggle must be <button>, found <${toggleBtn.tagName.toLowerCase()}>`);
    }
    
    const toggleId = ensureId(toggleBtn, `accordion-toggle-${index}`);
    const panelId = ensureId(panelEl, `accordion-panel-${index}`);
    
    toggleBtn.setAttribute('aria-controls', panelId);
    panelEl.setAttribute('aria-labelledby', toggleId);
    
    return {
      element: itemEl,
      index,
      toggleBtn,
      panelEl,
      hash: itemEl.id || null,
      isOpen: false,
      
      applyAria() {
        this.toggleBtn.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
        this.panelEl.setAttribute('role', 'region');
        this.panelEl.setAttribute('aria-hidden', this.isOpen ? 'false' : 'true');
      },
      
      removeAria() {
        this.toggleBtn.removeAttribute('aria-expanded');
        this.panelEl.removeAttribute('role');
        this.panelEl.removeAttribute('aria-hidden');
      },
      
      open(animate = true) {
        if (this.isOpen) return;
        this.isOpen = true;
        this.toggleBtn.setAttribute('aria-expanded', 'true');
        this.panelEl.setAttribute('aria-hidden', 'false');
        if (this.hash) {
          setHashToURL(this.hash);
        }
        accordion.emit(EVENTS.OPENED, {
          index: this.index,
          hash: this.hash,
          animated: animate
        });
      },
      
      close(animate = true) {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.panelEl.setAttribute('aria-hidden', 'true');
        accordion.emit(EVENTS.CLOSED, {
          index: this.index,
          animated: animate
        });
      }
    };
  }
  
  #belongsToAccordion(element) {
    if (!element || !this.contains(element)) return false;
    let parent = element.parentElement;
    while (parent && parent !== this) {
      if (parent.tagName === 'LIFTR-ACCORDION') {
        return false;
      }
      parent = parent.parentElement;
    }
    return true;
  }
  
  #findDirectRole(container, roleName) {
    const candidates = [...container.querySelectorAll(`[${ATTRIBUTES.ROLE}~="${roleName}"]`)];
    return candidates.find(el => {
      if (!container.contains(el)) return false;
      if (!this.#belongsToAccordion(el)) return false;
      let parent = el.parentElement;
      while (parent && parent !== container) {
        const parentRoles = parent.getAttribute(ATTRIBUTES.ROLE);
        if (parentRoles && parentRoles.split(/\s+/).includes(ROLES.ITEM) && parent !== container) {
          return false;
        }
        parent = parent.parentElement;
      }
      return true;
    }) || null;
  }
  
  /**
   * Read CSS-driven setting. Single source: CSS_SCHEMA via watchSettings.
   * @param {string} key - CSS_SCHEMA key (mode, exclusive, icon, size)
   * @returns {*} Current value or CSS_SCHEMA default
   */
  #css(key) {
    return this.#cssSettings?.[key] ?? CSS_SCHEMA[key]?.default;
  }
  
  #setupItems() {
    const itemElements = this.roleAll(ROLES.ITEM).filter(el => this.#belongsToAccordion(el));
    if (itemElements.length === 0) {
      throw new Error(`No items found with liftr-role="${ROLES.ITEM}"`);
    }
    this.#items = itemElements.map((el, i) => this.#createItem(el, i));
  }
  
  #attachListeners() {
    this.#items.forEach(item => {
      const handler = (e) => this.#handleToggleClick(e);
      item.toggleBtn.addEventListener('click', handler);
      this.addDisposable(() => item.toggleBtn.removeEventListener('click', handler));
    });
    
    const keyHandler = (e) => this.#handleKeyDown(e);
    this.addEventListener('keydown', keyHandler);
    this.addDisposable(() => this.removeEventListener('keydown', keyHandler));
    
    const hashHandler = () => this.#handleHashChange();
    window.addEventListener('hashchange', hashHandler);
    this.addDisposable(() => window.removeEventListener('hashchange', hashHandler));
  }
  
  #initialSetup() {
    const mode = this.#css('mode');
    const icon = this.#css('icon');
    const size = this.#css('size');
    
    // Sheet mode: no inline ARIA, start panel close listener
    if (mode === 'sheet') {
      this.#items.forEach(item => item.removeAria());
      this.#applySettings(mode, icon, size);
      this.#listenPanelClose();
      this.#hasInitialized = true;
      return;
    }
    
    this.#items.forEach(item => item.applyAria());
    this.#applySettings(mode, icon, size);
    
    if (mode === 'closed') {
      const hash = getHashFromURL();
      if (hash) {
        const item = this.#items.find(i => i.hash === hash);
        if (item) {
          this.#openItem(item.index, false);
        }
      } else if (typeof this.config.initialOpen === 'number') {
        const index = Math.max(0, Math.min(this.#items.length - 1, this.config.initialOpen));
        this.#openItem(index, false);
      }
    }
    
    this.#hasInitialized = true;
  }
  
  #openItem(index, animate = true) {
    const item = this.#items[index];
    if (!item) return;
    const exclusive = this.#css('exclusive');
    if (exclusive === true) {
      this.#items.forEach((otherItem, i) => {
        if (i !== index && otherItem.isOpen) {
          otherItem.close(animate);
        }
      });
    }
    item.open(animate);
  }
  
  #closeItem(index, animate = true) {
    const item = this.#items[index];
    if (!item) return;
    item.close(animate);
  }
  
  #applySettings(mode, icon, size) {
    const previousMode = this.#currentMode;
    this.#currentMode = mode;
    this.#currentIcon = icon;
    this.#currentSize = size;
    
    const tokens = buildSettingsTokens(mode, icon, size);
    this.setAttribute(ATTRIBUTES.SETTINGS, tokens);
    
    // Mode transitions
    if (previousMode !== mode) {
      // Entering sheet mode
      if (mode === 'sheet') {
        this.closeAll();
        this.#items.forEach(item => item.removeAria());
        this.#listenPanelClose();
        return;
      }
      
      // Leaving sheet mode
      if (previousMode === 'sheet') {
        this.#closeActiveSheet();
        this.#destroyAllSheets();
        this.#unlistenPanelClose();
        this.#items.forEach(item => item.applyAria());
      }
    }
    
    // Highlight mode: first panel always open
    if (mode === 'highlight') {
      if (this.#items.length > 0 && !this.#items[0].isOpen) {
        this.#items[0].open(false);
      }
    } else if (previousMode === 'highlight') {
      if (this.#items.length > 0 && this.#items[0].isOpen) {
        this.#items[0].close(false);
      }
    }
  }
  
  #onEnable(s) {
    this.#isDisabled = false;
    this.#cssSettings = s.css || {};
    
    const mode = this.#css('mode');
    const icon = this.#css('icon');
    const size = this.#css('size');
    
    // Sheet mode: no inline ARIA
    if (mode === 'sheet') {
      this.#items.forEach(item => item.removeAria());
    } else {
      this.#items.forEach(item => item.applyAria());
    }
    
    this.#applySettings(mode, icon, size);
    
    if (!this.#hasInitialized && mode === 'closed') {
      const hash = getHashFromURL();
      if (hash) {
        const item = this.#items.find(i => i.hash === hash);
        if (item) {
          this.#openItem(item.index, false);
        }
      } else if (typeof this.config.initialOpen === 'number') {
        const index = Math.max(0, Math.min(this.#items.length - 1, this.config.initialOpen));
        this.#openItem(index, false);
      }
      this.#hasInitialized = true;
    }
    
    this.emit(EVENTS.ENABLED, { enabled: true });
  }
  
  #onDisable() {
    this.#isDisabled = true;
    
    // Sheet cleanup
    this.#closeActiveSheet();
    this.#destroyAllSheets();
    this.#unlistenPanelClose();
    
    this.removeAttribute(ATTRIBUTES.SETTINGS);
    this.#items.forEach(item => item.removeAria());
    this.emit(EVENTS.DISABLED, { enabled: false });
  }
  
  #onChange(changes) {
    if (this.#isDisabled) return;
    
    if (changes.initial) {
      this.#cssSettings = changes.css || {};
      return;
    }
    
    if (changes.css) {
      const oldMode = this.#currentMode;
      const oldIcon = this.#currentIcon;
      const oldSize = this.#currentSize;
      
      this.#cssSettings = changes.css;
      
      const newMode = this.#css('mode');
      const newIcon = this.#css('icon');
      const newSize = this.#css('size');
      
      if (newMode !== oldMode || newIcon !== oldIcon || newSize !== oldSize) {
        this.#applySettings(newMode, newIcon, newSize);
        
        this.emit(EVENTS.SETTINGS_CHANGED, {
          mode: newMode,
          icon: newIcon,
          size: newSize,
          exclusive: this.#css('exclusive')
        });
      }
    }
  }
  
  #handleToggleClick(e) {
    if (this.#isDisabled) return;
    
    const button = e.currentTarget;
    const itemEl = button.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM}"]`);
    if (!itemEl) return;
    if (!this.#belongsToAccordion(itemEl)) return;
    
    const item = this.#items.find(i => i.element === itemEl);
    if (!item) return;
    
    // Sheet mode: open as bottom sheet
    if (this.#currentMode === 'sheet') {
      this.#openSheet(item.index);
      return;
    }
    
    // Inline mode: toggle open/close
    if (item.isOpen) {
      this.#closeItem(item.index);
    } else {
      this.#openItem(item.index);
    }
  }
  
  #handleKeyDown(e) {
    if (this.#isDisabled) return;
    
    const toggle = e.target;
    if (toggle.tagName !== 'BUTTON') return;
    
    const roles = toggle.getAttribute(ATTRIBUTES.ROLE);
    if (!roles || !roles.split(/\s+/).includes(ROLES.TOGGLE)) return;
    if (!this.#belongsToAccordion(toggle)) return;
    
    const currentIndex = this.#items.findIndex(item => item.toggleBtn === toggle);
    if (currentIndex === -1) return;
    
    let handled = false;
    
    switch (e.key) {
      case KEYS.ARROW_DOWN:
        e.preventDefault();
        handled = true;
        this.#focusNextToggle(currentIndex);
        break;
      case KEYS.ARROW_UP:
        e.preventDefault();
        handled = true;
        this.#focusPrevToggle(currentIndex);
        break;
      case KEYS.HOME:
        e.preventDefault();
        handled = true;
        this.#focusFirstToggle();
        break;
      case KEYS.END:
        e.preventDefault();
        handled = true;
        this.#focusLastToggle();
        break;
    }
    
    if (handled) {
      this.emit(EVENTS.KEYBOARD, {
        key: e.key,
        fromIndex: currentIndex
      });
    }
  }
  
  #handleHashChange() {
    if (this.#isDisabled) return;
    const hash = getHashFromURL();
    if (!hash) return;
    const item = this.#items.find(i => i.hash === hash);
    if (item) {
      this.#openItem(item.index);
    }
  }
  
  #focusNextToggle(currentIndex) {
    const nextIndex = (currentIndex + 1) % this.#items.length;
    this.#items[nextIndex].toggleBtn.focus();
  }
  
  #focusPrevToggle(currentIndex) {
    const prevIndex = currentIndex === 0 ? this.#items.length - 1 : currentIndex - 1;
    this.#items[prevIndex].toggleBtn.focus();
  }
  
  #focusFirstToggle() {
    if (this.#items.length > 0) {
      this.#items[0].toggleBtn.focus();
    }
  }
  
  #focusLastToggle() {
    if (this.#items.length > 0) {
      this.#items[this.#items.length - 1].toggleBtn.focus();
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * SHEET MODE — PANEL CREATION
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Create a sheet panel on demand for the given item index.
   * Panel is inserted inside the accordion-item element,
   * keeping filter inputs as children of liftr-list-filter.
   * @param {number} index
   * @returns {{ panel: HTMLElement, placeholder: Comment }}
   * @private
   */
  #createSheet(index) {
    const item = this.#items[index];
    
    // Derive panel ID from toggle's liftr-target or generate one
    const sheetId = item.toggleBtn.getAttribute(ATTRIBUTES.TARGET)
      || `${this.getAttribute(ATTRIBUTES.ID) || 'accordion'}-sheet-${index}`;
    
    // Create panel element
    const panel = document.createElement('liftr-panel');
    panel.style.setProperty('--panel-mode', 'sheet');
    panel.style.setProperty('--panel-enabled', 'true');
    panel.setAttribute(ATTRIBUTES.ID, sheetId);
    
    // Clone close icon from nearest existing panel close button
    const PR = this.#panelRoles;
    const existingCloseBtn = PR
      ? this.closest('liftr-panel')?.querySelector(`[${ATTRIBUTES.ROLE}~="${PR.CLOSE}"]`)
      : null;
    const closeIconHTML = existingCloseBtn?.innerHTML ?? '';
    
    // Get title from toggle's first text content (before any child elements)
    let titleText = '';
    for (const node of item.toggleBtn.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        titleText += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Take text from first element (legend, span, etc.) but stop at badges/pills
        if (!node.hasAttribute(ATTRIBUTES.ROLE)) {
          titleText += node.textContent;
        }
      }
    }
    titleText = titleText.trim();
    
    // Get i18n close label
    const closeLabel = i18n.get('panel')?.close ?? 'Close';
    const saveLabel = i18n.get('panel')?.save ?? 'Speichern';
    
    panel.innerHTML = [
      '<dialog class="panel_component">',
      `  <div ${ATTRIBUTES.ROLE}="${PR?.DRAG ?? 'panel-drag'}"></div>`,
      '  <header class="panel_header">',
      `    <h3 class="panel_title">${titleText} <span liftr-role="filter-count" class="count-pill"></span></h3>`,
      `    <button ${ATTRIBUTES.ROLE}="${PR?.CLOSE ?? 'panel-close'}" class="panel_action" type="button" aria-label="${closeLabel}">`,
      `      ${closeIconHTML}`,
      '    </button>',
      '  </header>',
      '  <div class="panel_module"></div>',
      '  <footer class="panel_footer">',
      `    <button ${ATTRIBUTES.ROLE}="${PR?.CLOSE ?? 'panel-close'}" class="button" type="button">${saveLabel}</button>`,
      '  </footer>',
      '</dialog>'
    ].join('\n');
    
    // Insert panel inside the item (after the accordion-panel)
    if (item.panelEl) {
      item.panelEl.after(panel);
    } else {
      item.element.appendChild(panel);
    }
    
    const placeholder = document.createComment(`sheet-${index}`);
    const moduleEl = panel.querySelector('.panel_module');
    const entry = { panel, placeholder, moduleEl };
    this.#sheets.set(index, entry);
    
    return entry;
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * SHEET MODE — OPEN / CLOSE
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Open a sheet for the given item index.
   * Portals accordion-panel into the sheet's .panel_module.
   * @param {number} index
   * @private
   */
  async #openSheet(index) {
    const item = this.#items[index];
    if (!item?.panelEl) return;
    
    // Ensure panel ROLES are loaded (cached after first call)
    await this.#loadPanelRoles();
    
    // Close currently open sheet first (if different item)
    if (this.#activeSheetIndex !== null && this.#activeSheetIndex !== index) {
      this.#closeActiveSheet();
    }
    
    // Already open and actually visible — do nothing
    if (this.#activeSheetIndex === index) {
      const sheet = this.#sheets.get(index);
      if (sheet?.panel?.isOpen) return;
      // Panel is closing or closed — reset tracking, allow reopen
      this.#returnSheetContent(index);
      this.#activeSheetIndex = null;
    }
    
    // Get or create sheet
    const sheet = this.#sheets.get(index) || this.#createSheet(index);
    if (!sheet.moduleEl) return;
    
    // Portal: place marker, move accordion-panel into sheet module
    item.panelEl.before(sheet.placeholder);
    sheet.moduleEl.appendChild(item.panelEl);
    
    this.#activeSheetIndex = index;
    
    // Open panel via Public API (wait for CE upgrade if needed)
    customElements.whenDefined('liftr-panel').then(() => {
      sheet.panel.open?.();
    });
    
    this.emit(EVENTS.OPENED, { index, mode: 'sheet' });
  }
  
  /**
   * Return portaled content from sheet back to accordion item.
   * @param {number} index
   * @private
   */
  #returnSheetContent(index) {
    const sheet = this.#sheets.get(index);
    const item = this.#items[index];
    if (!sheet || !item) return;
    
    // Find the accordion-panel inside the sheet's module
    const panelEl = sheet.moduleEl?.querySelector(`[${ATTRIBUTES.ROLE}~="${ROLES.PANEL}"]`);
    
    if (panelEl && sheet.placeholder.parentNode) {
      sheet.placeholder.after(panelEl);
      sheet.placeholder.remove();
    }
  }
  
  /**
   * Close the currently active sheet (portal content back, close panel).
   * @private
   */
  #closeActiveSheet() {
    if (this.#activeSheetIndex === null) return;
    
    const sheet = this.#sheets.get(this.#activeSheetIndex);
    const closedIndex = this.#activeSheetIndex;
    
    // Return content first
    this.#returnSheetContent(this.#activeSheetIndex);
    this.#activeSheetIndex = null;
    
    // Close panel if open
    if (sheet?.panel?.isOpen) {
      sheet.panel.close?.();
    }
    
    this.emit(EVENTS.CLOSED, { index: closedIndex, mode: 'sheet' });
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * SHEET MODE — PANEL CLOSE LISTENER
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Handle panel:closed event from sheet panels.
   * @param {CustomEvent} e
   * @private
   */
  #handlePanelClosed(e) {
    const sheet = this.#activeSheetIndex !== null
      ? this.#sheets.get(this.#activeSheetIndex)
      : null;
    
    // If no active sheet, check all sheets (panel may have closed during reopen race)
    if (!sheet) {
      for (const [index, s] of this.#sheets) {
        const id = s.panel.getAttribute(ATTRIBUTES.ID);
        if (e.detail?.id === id) {
          // Content may already be returned — safe to call, #returnSheetContent checks placeholder
          this.#returnSheetContent(index);
          return;
        }
      }
      return;
    }
    
    const sheetId = sheet.panel.getAttribute(ATTRIBUTES.ID);
    if (e.detail?.id !== sheetId) return;
    
    const closedIndex = this.#activeSheetIndex;
    this.#returnSheetContent(this.#activeSheetIndex);
    this.#activeSheetIndex = null;
    
    this.emit(EVENTS.CLOSED, { index: closedIndex, mode: 'sheet' });
  }
  
  /**
   * Lazy-load panel module ROLES (only when sheet mode is needed).
   * @returns {Promise<Object>}
   * @private
   */
  async #loadPanelRoles() {
    if (this.#panelRoles) return this.#panelRoles;
    const { ROLES: PANEL_ROLES } = await import('./ui.panel.js');
    this.#panelRoles = PANEL_ROLES;
    return PANEL_ROLES;
  }
  /**
   * Start listening for panel:closed events (sheet mode).
   * Also preloads panel module ROLES.
   * @private
   */
  #listenPanelClose() {
    if (this.#panelCloseDisposable) return;
    this.#loadPanelRoles();
    this.#panelCloseDisposable = on(LISTEN.PANEL_CLOSED, (e) => {
      this.#handlePanelClosed(e);
    });
    this.addDisposable(this.#panelCloseDisposable);
  }
  
  /**
   * Stop listening for panel:closed events.
   * @private
   */
  #unlistenPanelClose() {
    this.#panelCloseDisposable?.();
    this.#panelCloseDisposable = null;
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * SHEET MODE — CLEANUP
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Destroy all sheet panels (on mode change or unmount).
   * Returns portaled content first.
   * @private
   */
  #destroyAllSheets() {
    for (const [index, sheet] of this.#sheets) {
      this.#returnSheetContent(index);
      sheet.panel.remove();
    }
    this.#sheets.clear();
    this.#activeSheetIndex = null;
  }
  
  open(index) {
    if (!this.#isDisabled) {
      this.#openItem(index, true);
    }
  }
  
  close(index) {
    if (!this.#isDisabled) {
      this.#closeItem(index, true);
    }
  }
  
  toggle(index) {
    const item = this.#items[index];
    if (item && !this.#isDisabled) {
      item.isOpen ? this.#closeItem(index) : this.#openItem(index);
    }
  }
  
  closeAll() {
    if (!this.#isDisabled) {
      this.#items.forEach((item, i) => {
        if (item.isOpen) {
          this.#closeItem(i, true);
        }
      });
    }
  }
  
  get length() {
    return this.#items.length;
  }
  
  isOpen(index) {
    return this.#items[index]?.isOpen ?? false;
  }
  
  get openIndex() {
    return this.#items.findIndex(item => item.isOpen);
  }
  
  get openIndices() {
    return this.#items
      .map((item, i) => item.isOpen ? i : -1)
      .filter(i => i !== -1);
  }
}

customElements.define('liftr-accordion', LiftrAccordion);

export { LiftrAccordion, ROLES, STATES, EVENTS, SETTINGS };